import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface AuthCallbackProps {
  onSuccess: () => void;
  onError: (error: string) => void;
}

export function AuthCallback({ onSuccess, onError }: AuthCallbackProps) {
  const [status, setStatus] = useState('Signing you in...');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      exchangeToken(token);
    } else {
      setStatus('No token provided');
      onError('No token provided');
    }
  }, []);

  async function exchangeToken(token: string) {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/exchange-token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Token exchange failed');
      }

      const { access_token, refresh_token } = await response.json();

      // Set the session in Supabase
      const { error: sessionError } = await supabase.auth.setSession({
        access_token,
        refresh_token
      });

      if (sessionError) {
        throw sessionError;
      }

      // Clear the token from URL
      window.history.replaceState({}, '', '/');

      setStatus('Success! Redirecting...');
      onSuccess();
    } catch (error) {
      console.error('Token exchange error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign in';
      setStatus(`Sign in failed: ${errorMessage}`);
      onError(errorMessage);
    }
  }

  return (
    <div className="auth-callback">
      <div className="auth-callback-content">
        <div className="loading-spinner" />
        <p>{status}</p>
      </div>
      <style>{`
        .auth-callback {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-primary, #0a0a0a);
          color: var(--text-primary, #ffffff);
        }
        .auth-callback-content {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }
        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(249, 115, 22, 0.2);
          border-top-color: var(--accent, #f97316);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
